import 'whatwg-fetch';
import './webpack-public-path';
//  import fetchIntercept from 'fetch-intercept';

import { datadogRum } from '@datadog/browser-rum';
import { Search } from '@kyruus/search-sdk';
import loadable from '@loadable/component';
import axios from 'axios';
import { func, object } from 'prop-types';
import React, {
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import { matchPath, Switch, withRouter } from 'react-router-dom';
import { maplibre } from '@kyruus/aws-map';
import {
  getConfigProperty,
  shouldRenderDirectBookInDrawer
} from 'Common/config';
import '../stylesheets/providermatch-consumer.scss';
import { AuthenticatedUserInfoProvider } from './hooks/useAuthenticatedUserInfo';
import {
  CrmConnectorProvider,
  ReferralConnectorProvider
} from './referral-crm-connector';
import { AppDrawerProvider } from './app-drawer';
import { AppReadySignal } from './app-ready-signal';
import { loadPersistedTokensIntoStorage } from './tracking/persisted-tokens';
import { persistThirdPartyTokens } from './utils/third_party';
import { ROUTE_PATHS } from './utils/constants';
import { getLoginRedirectUrl } from './utils/url';

import CustomConfigActiveBanner from './editor/custom-config-active-banner';
import {
  AppointmentCancelRoute,
  DirectBookDrawerRoute,
  DirectBookRoute,
  LocationRoute,
  ManageAppointmentRoute,
  MapSearchRoute,
  ProfileRoute,
  SearchMatchRoute,
  WaitlistRoute
} from './routes';
import LoadingOverlay from './shared/loading';

import DBookingContainer from './containers/direct-booking-container';

const HomeContainer = loadable(() => import('./containers/home-container'));
const HomeContainerV9 = loadable(
  () => import('./containers/home-container-v9')
);
const ProfileContainer = loadable(
  () => import('./containers/profile-container')
);
const AppointmentCancelContainer = loadable(
  () => import('./containers/appointment-cancel-container')
);
const LocationContainer = loadable(
  () => import('./containers/location-container')
);
const MapSearchContainer = loadable(
  () => import('./containers/map-search-container')
);
const MapSearchContainerV9 = loadable(
  () => import('./containers/map-search-container-v9')
);
const ManageAppointmentContainer = loadable(
  () => import('./containers/manage-appointment-container')
);
const WaitistContainer = loadable(
  () => import('./containers/waitlist-container')
);

const ClientApp = withRouter(
  ({
    log,
    config,
    customerCode,
    authenticatedUserInfo,
    location,
    productName
  }) => {
    const interceptRequestSuccess = (config) => {
      config.headers = {
        ...config.headers,
        // value doesn't matter, the header just needs to exist on the request
        'x-csrf-header': customerCode,
        'X-Requested-With': 'XMLHttpRequest'
      };
      return config;
    };
    const interceptError = (error) => {
      return Promise.reject(error);
    };
    // axios interceptors
    axios.interceptors.request.use(interceptRequestSuccess, interceptError);
    axios.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        if (
          (error.status === 403 || error.response?.status === 403) &&
          (error.data?.loginServiceUrl || error.response?.data?.loginServiceUrl)
        ) {
          const loginServiceUrl =
            error.data?.loginServiceUrl ||
            error.response?.data?.loginServiceUrl;
          const loginRedirectUrl = getLoginRedirectUrl(
            window.location,
            loginServiceUrl
          );
          return window.location.replace(loginRedirectUrl);
        }
        return Promise.reject(error);
      }
    );
    // Disabling this feature for release 9.0.0 (KENG-40098) as it intereferes with a fetch request
    // to youtube in the provider-profile-component package and breaks provider videos on the profile
    // leaving code commented here for the followup fix to use
    //
    // fetch interceptors
    // fetchIntercept.register({
    //   request: function (url, config) {
    //     config = interceptRequestSuccess(config);
    //     return [url, config];
    //   },
    //   requestError: interceptError,
    //   response: async function (response) {
    //     if (response.status === 403) {
    //       const { loginServiceUrl } = await response.json();
    //       const loginRedirectUrl = getLoginRedirectUrl(
    //         window.location,
    //         loginServiceUrl
    //       );
    //       return window.location.replace(loginRedirectUrl);
    //     }
    //     return response;
    //   },
    //   responseError: interceptError
    // });

    const workerUrl = '/aws-maps/maplibre-gl-csp-worker.js';

    if (
      getConfigProperty(config, 'maps_config.location_service_provider') ===
      'aws'
    ) {
      maplibre.setWorkerUrl(workerUrl);
    }

    const searchSDK = useRef(
      new Search({
        customerCode,
        productName,
        baseURL: '/api/searchservice-v9'
      })
    );

    const [caseId, setCaseId] = useState(null);

    // load tokens from/to localstorage. We need to do this early here because they're only
    // available on the client. These tokens are 'persisted' in that they should be appended
    // to cross-domain urls for tracking purposes, for example from search to DBW
    loadPersistedTokensIntoStorage();

    // persist third-party patient tokens (cerner bcs_token, signed patient token) from the URL
    // to localStorage, since client-side route changes drop the query string
    persistThirdPartyTokens(config);

    // If direct_book.render_type is 'drawer', preload the DBW container
    // to avoid flash of white content while it loads since
    // it is rendered within a drawer using react-router modal route
    // instead of standard route.
    // See https://v5.reactrouter.com/web/example/modal-gallery for more info.
    const renderDirectBookInDrawer = shouldRenderDirectBookInDrawer(config);

    useEffect(() => {
      const matchedRoute = matchPath(
        location.pathname,
        Object.values(ROUTE_PATHS)
      );
      if (matchedRoute) {
        datadogRum.startView({ name: matchedRoute.path });
      }
    }, [location.pathname]);

    const memoLog = useCallback(
      (eventName, eventData) => {
        let data = eventData;

        if (caseId) {
          data = { ...data, case_id: caseId };
        }

        log(eventName, data);
      },
      [caseId, log]
    );

    const [isLoading, setIsLoading] = useState(false);

    const extractProviderIdFromUrl = (pathname) => {
      if (pathname.startsWith('/book/')) {
        const pathParts = pathname.split('/');
        return pathParts[pathParts.length - 1];
      }
      return null;
    };

    const providerId = extractProviderIdFromUrl(location.pathname);

    const fetchProviderData = async () => {
      setIsLoading(true);
      try {
        const params = {
          id: providerId,
          context: config.index || customerCode
        };
        const response = await searchSDK.current.getProviderById(params);

        const providerName = response.data.provider?.name?.full;
        const encodedName = encodeURIComponent(providerName);
        const searchQueryParams = `?provider_name=${encodedName}&sort=relevance`;

        location.state = {
          provider: {
            location: {
              hash: '',
              key: '',
              pathname: '/search',
              search: searchQueryParams,
              state: undefined
            },
            provider: response.data.provider
          }
        };
      } catch (error) {
        memoLog('provider_fetch_error', {
          error: error,
          providerId,
          endpoint: 'getProviderById'
        });
      } finally {
        setIsLoading(false);
      }
    };

    useEffect(() => {
      if (!location.state && providerId && renderDirectBookInDrawer) {
        fetchProviderData();
      }
    }, []);

    if (
      providerId &&
      isLoading &&
      renderDirectBookInDrawer &&
      !location.state
    ) {
      return <LoadingOverlay loading={true} />;
    }

    const provider = location.state?.provider;

    return (
      <AuthenticatedUserInfoProvider userInfo={authenticatedUserInfo}>
        <AppDrawerProvider>
          <CrmConnectorProvider
            config={config}
            log={memoLog}
            machineOptions={{
              actions: {
                onPatientReceived: setCaseId
              }
            }}
          >
            <ReferralConnectorProvider config={config} log={memoLog}>
              <Suspense fallback={<LoadingOverlay loading={true} />}>
                {/* If modal routing location exists, use it. otherwise, fallback to default location. */}
                <Switch location={provider?.location || location}>
                  <ProfileRoute
                    config={config}
                    log={memoLog}
                    path={ROUTE_PATHS.PROFILE}
                    component={ProfileContainer}
                    searchSDK={searchSDK}
                  />
                  {!provider && (
                    <DirectBookRoute
                      config={config}
                      log={memoLog}
                      path={ROUTE_PATHS.DIRECT_BOOK}
                      component={DBookingContainer}
                      searchSDK={searchSDK}
                    />
                  )}
                  <AppointmentCancelRoute
                    config={config}
                    log={memoLog}
                    path={ROUTE_PATHS.APPOINTMENT_CANCEL}
                    component={AppointmentCancelContainer}
                    searchSDK={searchSDK}
                  />
                  <ManageAppointmentRoute
                    config={config}
                    log={memoLog}
                    path={ROUTE_PATHS.MANAGE_APPOINTMENT}
                    component={ManageAppointmentContainer}
                    searchSDK={searchSDK}
                  />
                  <WaitlistRoute
                    config={config}
                    log={memoLog}
                    path={ROUTE_PATHS.WAITLIST_APPOINTMENT}
                    component={WaitistContainer}
                    searchSDK={searchSDK}
                  />
                  <LocationRoute
                    config={config}
                    log={memoLog}
                    path={ROUTE_PATHS.LOCATION}
                    component={LocationContainer}
                    searchSDK={searchSDK}
                  />
                  <MapSearchRoute
                    config={config}
                    log={memoLog}
                    path={ROUTE_PATHS.MAP_SEARCH}
                    component={
                      config.darkship_use_list_page_searchv9
                        ? MapSearchContainerV9
                        : MapSearchContainer
                    }
                  />
                  <SearchMatchRoute
                    config={config}
                    log={memoLog}
                    path={ROUTE_PATHS.SEARCH_MATCH}
                    component={
                      config.darkship_use_list_page_searchv9
                        ? HomeContainerV9
                        : HomeContainer
                    }
                  />
                </Switch>

                {/* Render the DBW modal route for use within a drawer. Uses the data provided in the location state in agent mode.
                Drawer always exists in the DOM in agent mode (to allow enter/exit animations to work properly) but is not accessible without
                the location state mentioned.
              */}
                {renderDirectBookInDrawer && (
                  <DirectBookDrawerRoute
                    config={config}
                    log={memoLog}
                    path={ROUTE_PATHS.DIRECT_BOOK}
                    component={DBookingContainer}
                    searchSDK={searchSDK}
                    provider={provider?.provider}
                  />
                )}
                <CustomConfigActiveBanner
                  authenticatedUserInfo={authenticatedUserInfo}
                />
                {/* inside the boundary so it fires once the page content hydrates */}
                <AppReadySignal />
              </Suspense>
            </ReferralConnectorProvider>
          </CrmConnectorProvider>
        </AppDrawerProvider>
      </AuthenticatedUserInfoProvider>
    );
  }
);

ClientApp.propTypes = {
  // logging function connected to `@kyruus/trackyr`
  log: func.isRequired,
  // config from store state
  config: object.isRequired
};

export { ClientApp };
